/**
 * Centro por defecto de los mapas, POR PAÍS.
 *
 * `VITE_MAP_CENTER_LAT/LNG` vivían en el `.env` base, que es compartido por los
 * dos países, con las coordenadas de Montevideo fijas. Resultado: en el panel
 * de Perú TODOS los mapas —alta de pedido, zonas, ubicación manual— abrían
 * sobre Uruguay y había que arrastrar el mapa medio continente antes de poder
 * marcar nada. Por eso el centro sale del país del build y no del env.
 */
const COUNTRY = String(import.meta.env.VITE_COUNTRY ?? '').toLowerCase();

/** Plaza de Armas (Lima) y Plaza Independencia (Montevideo). */
const CENTER_BY_COUNTRY: Record<string, { lat: number; lng: number }> = {
  pe: { lat: -12.0464, lng: -77.0428 },
  uy: { lat: -34.9011, lng: -56.1645 },
};

const DEFAULT_CENTER = CENTER_BY_COUNTRY[COUNTRY] ?? CENTER_BY_COUNTRY.uy;

/**
 * Un env vacío o mal escrito daba `NaN`, y los mapas que no chequean
 * `Number.isFinite` quedaban en blanco en vez de caer al centro del país.
 */
const numberFromEnv = (raw: unknown, fallback: number): number => {
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed !== 0 ? parsed : fallback;
};

export const MAP_CENTER_LAT = numberFromEnv(
  import.meta.env.VITE_MAP_CENTER_LAT,
  DEFAULT_CENTER.lat
);
export const MAP_CENTER_LNG = numberFromEnv(
  import.meta.env.VITE_MAP_CENTER_LNG,
  DEFAULT_CENTER.lng
);
export const MAP_DEFAULT_ZOOM = numberFromEnv(
  import.meta.env.VITE_MAP_DEFAULT_ZOOM,
  13
);
export const DEFAULT_ZONE_COLOR = String(
  import.meta.env.VITE_DEFAULT_ZONE_COLOR ?? '#3388ff'
);

/**
 * Caja aproximada del territorio operativo de cada país, con un margen para
 * cubrir la zona costera y las áreas fronterizas donde también se opera.
 *
 * Se usa para dos cosas en el mapa de zonas:
 *  - Restringir el paneo con `maxBounds` (que no se pueda arrastrar a otro
 *    continente).
 *  - Descartar geometrías que llegan corruptas del backend con coordenadas
 *    fuera del país (evita que un polígono roto empuje el fitBounds a la nada).
 */
const COUNTRY_MAP_BOUNDS: Record<
  string,
  { south: number; west: number; north: number; east: number }
> = {
  uy: { south: -36.5, west: -59.5, north: -29.5, east: -51.5 },
  pe: { south: -19, west: -83, north: 1, east: -67 },
};

export const MAP_BOUNDS =
  COUNTRY_MAP_BOUNDS[COUNTRY] ?? COUNTRY_MAP_BOUNDS.uy;

export const isInCountryRegion = (lat: number, lng: number): boolean =>
  lat >= MAP_BOUNDS.south &&
  lat <= MAP_BOUNDS.north &&
  lng >= MAP_BOUNDS.west &&
  lng <= MAP_BOUNDS.east;

function isPointInRing(
  pointLat: number,
  pointLng: number,
  ring: number[][]
): boolean {
  if (!ring || ring.length < 3) return false;
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    const intersect =
      yi > pointLat !== yj > pointLat &&
      pointLng < ((xj - xi) * (pointLat - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

export type GeoJsonPolygonLike =
  | { type: 'Polygon'; coordinates: number[][][] }
  | { type: 'MultiPolygon'; coordinates: number[][][][] };

export const isPointInPolygon = (
  pointLat: number,
  pointLng: number,
  polygon: GeoJsonPolygonLike | null | undefined
): boolean => {
  if (!polygon || !polygon.coordinates) return false;

  if (polygon.type === 'Polygon') {
    return isPointInRing(pointLat, pointLng, polygon.coordinates[0]);
  }

  if (polygon.type === 'MultiPolygon') {
    return polygon.coordinates.some((poly) =>
      isPointInRing(pointLat, pointLng, poly[0])
    );
  }

  return false;
};
